import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useIsManagerView, useViewMode } from '@plato/app-core/hooks/use-view-mode';
import { SalesMetricBasis } from '@plato/app-meta/sales-metric';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { CustomerStatusBadge } from '@plato/app-shared/components/customer-status-badge';
import { FromNow } from '@plato/app-shared/components/from-now';
import { MaskedContent } from '@plato/app-shared/components/masked-content';
import {
    MetricHintExplanation,
    useMetricHintOpenedTracking,
} from '@plato/app-shared/components/metric-explainer';
import { DateText } from '@plato/ui-legacy/components/common/date-time-text';
import { DoubleDeckerCell } from '@plato/ui-legacy/components/list-views/double-decker-cell';
import { IdAndNameCell } from '@plato/ui-legacy/components/list-views/id-and-name-cell';
import { MarginPercentage } from '@plato/ui-legacy/components/list-views/margin-percentage';
import { DataText } from '@plato/ui-legacy/components/typography/data-text';
import { DeltaText } from '@plato/ui-legacy/components/typography/delta-text';
import { DeltaTriangle } from '@plato/ui-legacy/components/typography/delta-triangle';
import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import type { ColumnDef } from '@plato/ui/components/data-table';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerQueryAggregatesOutput } from '../../../server/queries/customer-query-aggregates.ts';
import type { CustomerListItem } from '../../../server/queries/customer-query-many-with-filters.ts';
import { CustomerSortBy } from '../../../server/types/list-view-types.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';

export type CustomerColumnDef = ColumnDef<CustomerListItem, CustomerQueryAggregatesOutput, CustomerSortBy>;

export function useCustomerColumns(sortingField?: CustomerSortBy) {
    const { t } = useTranslation();
    const trackMetricHintOpened = useMetricHintOpenedTracking();
    const {
        canReadSalesRepAssignment,
        enablePlatoStatus,
        canReadGrossMargin,
        defaultTimeframe,
        defaultMetric,
    } = useAppConfig();
    const isManagerView = useIsManagerView();
    const [viewMode] = useViewMode();
    const sendEvent = useAnalytics();
    const showTtmColumns = defaultTimeframe === 'TTM';
    const showYtdColumns = defaultTimeframe === 'YTD';
    const isOrderSumMetric = defaultMetric === 'Order_Sum';
    const billingStatus = isOrderSumMetric ? SalesMetricBasis.ORDER_SUM : SalesMetricBasis.BILLED;

    const trackVisitTooltipOpened = useCallback(
        (column: 'visits_per_100k' | 'last_visit') => {
            sendEvent('customer_list_visit_column_tooltip_opened', {
                column,
                view_mode: viewMode,
            });
        },
        [sendEvent, viewMode],
    );

    const customerColumns: CustomerColumnDef[] = useMemo(
        () => [
            {
                accessorKey: 'customerName',
                header: t('table_header__name', {
                    ns: 'customers-list',
                }),
                cell: ({ row, cell }) => (
                    <IdAndNameCell
                        to={`/customers/${encodeURIComponent(cell.row.original.customerId)}`}
                        id={row.original.customerId}
                        title={cell.renderValue() as string}
                    />
                ),
                size: 250,
                meta: {
                    aiChat: { label: t('table_header__name', { ns: 'customers-list' }) },
                    sortBy: CustomerSortBy.CUSTOMER_NAME,
                    pinned: 'left',
                    summary: () => t('table_footer__sum', { ns: 'customers-list' }),
                },
            },
            {
                accessorKey: 'status',
                meta: {
                    sortBy: CustomerSortBy.STATUS,
                    enabled: enablePlatoStatus,
                    aiChat: {
                        label: t('table_header__status', { ns: 'customers-list' }),
                        description:
                            'Plato status from its order-activity model: new, active, at risk or dormant, inactive when marked so by hand in Plato, or blocked when the ERP blocks the customer',
                        requires: ['enablePlatoStatus'],
                    },
                    hint: (
                        <div className="flex flex-col gap-tight">
                            <p>{t('status_tooltip', { ns: 'customers-list' })}</p>
                            <ul className="list-inside list-disc space-y-tight">
                                <li>
                                    <strong>{t('status_new', { ns: 'customers-list' })}</strong> -{' '}
                                    {t('status_tooltip_new', { ns: 'customers-list' })}
                                </li>
                                <li>
                                    <strong>{t('status_active', { ns: 'customers-list' })}</strong> -{' '}
                                    {t('status_tooltip_active', { ns: 'customers-list' })}
                                </li>
                                <li>
                                    <strong>{t('status_at_risk', { ns: 'customers-list' })}</strong> -{' '}
                                    {t('status_tooltip_at_risk', { ns: 'customers-list' })}
                                </li>
                                <li>
                                    <strong>{t('status_dormant', { ns: 'customers-list' })}</strong> -{' '}
                                    {t('status_tooltip_dormant', { ns: 'customers-list' })}
                                </li>
                                <li>
                                    <strong>{t('status_inactive', { ns: 'customers-list' })}</strong> -{' '}
                                    {t('status_tooltip_inactive', { ns: 'customers-list' })}
                                </li>
                                <li>
                                    <strong>{t('status_blocked', { ns: 'customers-list' })}</strong> -{' '}
                                    {t('status_tooltip_blocked', { ns: 'customers-list' })}
                                </li>
                            </ul>
                        </div>
                    ),
                },
                header: t('table_header__status', {
                    ns: 'customers-list',
                }),
                cell: ({ row }) => <CustomerStatusBadge status={row.original.platoStatus} />,
                size: 120,
            },
            {
                accessorKey: 'documentedVisits',
                header: t('table_header__visit_count', {
                    ns: 'customers-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <NumberOrEmpty decimals={0}>{row.original.documentedVisits}</NumberOrEmpty>
                    </DataText>
                ),
                meta: {
                    sortBy: CustomerSortBy.DOCUMENTED_VISITS,
                    enabled: isManagerView,
                    aiChat: {
                        label: t('table_header__visit_count', { ns: 'customers-list' }),
                        description:
                            'Visits logged in Plato in the last 12 months, including visits where the customer was not reached',
                        managerOnly: true,
                    },
                    align: 'right',
                },
                size: 120,
            },
            {
                accessorKey: 'visitsPer100kRevenue',
                header: t('table_header__visits_per_100k', {
                    ns: 'customers-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <NumberOrEmpty decimals={1}>{row.original.visitsPer100kRevenue}</NumberOrEmpty>
                    </DataText>
                ),
                meta: {
                    sortBy: CustomerSortBy.VISITS_PER_100K,
                    enabled: isManagerView,
                    aiChat: {
                        label: t('table_header__visits_per_100k', { ns: 'customers-list' }),
                        description:
                            'Reached visits per 100k of billed revenue in the last 12 months. Empty below 1k revenue',
                        managerOnly: true,
                    },
                    align: 'right',
                    hint: t('visits_per_100k_tooltip', { ns: 'customers-list' }),
                    onHintOpen: () => {
                        trackVisitTooltipOpened('visits_per_100k');
                    },
                },
                size: 160,
            },
            {
                accessorKey: 'lastVisitDate',
                header: t('table_header__last_visit', {
                    ns: 'customers-list',
                }),
                cell: ({ row }) => {
                    const date = row.original.lastVisitDate;
                    if (!date) {
                        return <DataText>-</DataText>;
                    }
                    return (
                        <DoubleDeckerCell
                            align="right"
                            topClassName="text-neutral-500"
                            bottomClassName="text-neutral-950"
                            topLevel={<FromNow>{date}</FromNow>}
                            bottomLevel={<DateText>{date}</DateText>}
                        />
                    );
                },
                meta: {
                    sortBy: CustomerSortBy.LAST_VISIT,
                    enabled: isManagerView,
                    aiChat: {
                        label: t('table_header__last_visit', { ns: 'customers-list' }),
                        managerOnly: true,
                    },
                    align: 'right',
                    hint: t('last_visit_tooltip', { ns: 'customers-list' }),
                    onHintOpen: () => {
                        trackVisitTooltipOpened('last_visit');
                    },
                },
                size: 160,
            },
            {
                accessorKey: 'insightSumOpportunity',
                header: t('table_header__expansion_potential', {
                    ns: 'customers-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <CurrencyOrEmpty showEmptyIfZero>
                            {row.original.insightSumOpportunity}
                        </CurrencyOrEmpty>
                    </DataText>
                ),
                meta: {
                    sortBy: CustomerSortBy.INSIGHTS_SUM_OPPORTUNITY,
                    enabled: sortingField === CustomerSortBy.INSIGHTS_SUM_OPPORTUNITY,
                    aiChat: {
                        label: t('table_header__expansion_potential', { ns: 'customers-list' }),
                        description:
                            "Potential revenue of the customer's open expansion opportunities. Shown when sorting by it",
                    },
                    align: 'right',
                },
            },
            {
                accessorKey: 'insightSumRisk',
                header: t('table_header__revenue_decline', {
                    ns: 'customers-list',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <CurrencyOrEmpty showEmptyIfZero>{row.original.insightSumRisk}</CurrencyOrEmpty>
                    </DataText>
                ),
                meta: {
                    sortBy: CustomerSortBy.INSIGHTS_SUM_RISK,
                    enabled: sortingField === CustomerSortBy.INSIGHTS_SUM_RISK,
                    aiChat: {
                        label: t('table_header__revenue_decline', { ns: 'customers-list' }),
                        description:
                            "Revenue decline flagged by the customer's open churn warnings. Shown when sorting by it",
                    },
                    align: 'right',
                },
            },
            {
                accessorKey: 'revenueTtm',
                header: isOrderSumMetric
                    ? t('table_header__order_sum_ttm_current', {
                          ns: 'customers-list',
                      })
                    : t('table_header__ttm_current', { ns: 'customers-list' }),
                cell: ({ row }) => (
                    <DataText>
                        <CurrencyOrEmpty showEmptyIfZero>{row.original.revenueTtm}</CurrencyOrEmpty>
                    </DataText>
                ),
                meta: {
                    sortBy: CustomerSortBy.TTM,
                    enabled: showTtmColumns,
                    aiChat: {
                        label: isOrderSumMetric
                            ? t('table_header__order_sum_ttm_current', { ns: 'customers-list' })
                            : t('table_header__ttm_current', { ns: 'customers-list' }),
                    },
                    align: 'right',
                    summary: (data) => (
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>{data.revenueTtm}</CurrencyOrEmpty>
                        </DataText>
                    ),
                    hint: <MetricHintExplanation metric={billingStatus} />,
                    onHintOpen: () => {
                        trackMetricHintOpened(billingStatus);
                    },
                },
            },
            {
                accessorKey: 'deltaDifferenceTtm',
                header: t('table_header__ttm_delta', {
                    ns: 'customers-list',
                }),
                cell: ({ row }) => {
                    const amount = row.original.deltaDifferenceTtm;
                    const up = amount === 0 ? null : amount > 0;

                    return (
                        <DoubleDeckerCell
                            topLevel={
                                <DataText>
                                    <DeltaText up={up}>
                                        <PercentageOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                            {row.original.deltaDifferencePercentTtm}
                                        </PercentageOrEmpty>
                                        {up !== null && <DeltaTriangle up={up} />}
                                    </DeltaText>
                                </DataText>
                            }
                            bottomLevel={
                                <DataText>
                                    <CurrencyOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                        {row.original.deltaDifferenceTtm}
                                    </CurrencyOrEmpty>
                                </DataText>
                            }
                        />
                    );
                },
                meta: {
                    aiChat: { label: t('table_header__ttm_delta', { ns: 'customers-list' }) },
                    sortBy: CustomerSortBy.TTM_DELTA,
                    enabled: showTtmColumns,
                    align: 'right',
                    summary: (data) => {
                        const amount = data.deltaDifferenceTtm;
                        const up = amount === 0 ? null : amount > 0;
                        return (
                            <DoubleDeckerCell
                                topLevel={
                                    <DataText>
                                        <DeltaText up={up}>
                                            <PercentageOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                {data.deltaDifferencePercentTtm}
                                            </PercentageOrEmpty>
                                            {up !== null && <DeltaTriangle up={up} />}
                                        </DeltaText>
                                    </DataText>
                                }
                                bottomLevel={
                                    <DataText>
                                        <CurrencyOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                            {data.deltaDifferenceTtm}
                                        </CurrencyOrEmpty>
                                    </DataText>
                                }
                            />
                        );
                    },
                },
            },
            {
                accessorKey: 'revenueYtdThisYear',
                header: isOrderSumMetric
                    ? t('table_header__order_sum_ytd', { ns: 'customers-list' })
                    : t('table_header__revenue_ytd', { ns: 'customers-list' }),
                cell: ({ row }) => (
                    <DataText>
                        <CurrencyOrEmpty showEmptyIfZero>{row.original.revenueYtdThisYear}</CurrencyOrEmpty>
                    </DataText>
                ),
                meta: {
                    sortBy: CustomerSortBy.REVENUE_YTD,
                    enabled: showYtdColumns,
                    aiChat: {
                        label: isOrderSumMetric
                            ? t('table_header__order_sum_ytd', { ns: 'customers-list' })
                            : t('table_header__revenue_ytd', { ns: 'customers-list' }),
                    },
                    align: 'right',
                    summary: (data) => (
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>{data.revenueYtdThisYear}</CurrencyOrEmpty>
                        </DataText>
                    ),
                    hint: <MetricHintExplanation metric={billingStatus} />,
                    onHintOpen: () => {
                        trackMetricHintOpened(billingStatus);
                    },
                },
            },
            {
                accessorKey: 'revenueYtdDeltaAmount',
                header: t('table_header__revenue_ytd_delta', {
                    ns: 'customers-list',
                }),
                cell: ({ row }) => {
                    const amount = row.original.revenueYtdDeltaAmount;
                    const up = amount === 0 ? null : amount > 0;
                    return (
                        <DoubleDeckerCell
                            topLevel={
                                <DataText>
                                    <DeltaText up={up}>
                                        <PercentageOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                            {row.original.revenueYtdDeltaPercentage}
                                        </PercentageOrEmpty>
                                        {up !== null && <DeltaTriangle up={up} />}
                                    </DeltaText>
                                </DataText>
                            }
                            bottomLevel={
                                <DataText>
                                    <CurrencyOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                        {row.original.revenueYtdDeltaAmount}
                                    </CurrencyOrEmpty>
                                </DataText>
                            }
                        />
                    );
                },
                meta: {
                    aiChat: { label: t('table_header__revenue_ytd_delta', { ns: 'customers-list' }) },
                    sortBy: CustomerSortBy.REVENUE_YTD_DELTA,
                    enabled: showYtdColumns,
                    align: 'right',
                    summary: (data) => {
                        const amount = data.revenueYtdDeltaAmount;
                        const up = amount === 0 ? null : amount > 0;
                        return (
                            <DoubleDeckerCell
                                topLevel={
                                    <DataText>
                                        <DeltaText up={up}>
                                            <PercentageOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                {data.revenueYtdDeltaPercentage}
                                            </PercentageOrEmpty>
                                            {up !== null && <DeltaTriangle up={up} />}
                                        </DeltaText>
                                    </DataText>
                                }
                                bottomLevel={
                                    <DataText>
                                        <CurrencyOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                            {data.revenueYtdDeltaAmount}
                                        </CurrencyOrEmpty>
                                    </DataText>
                                }
                            />
                        );
                    },
                },
            },
            {
                accessorKey: 'lastOrderDate',
                header: t('table_header__last_order_date', {
                    ns: 'customers-list',
                }),
                cell: ({ row }) => {
                    return row.original.lastOrderDate ? (
                        <DoubleDeckerCell
                            align="left"
                            topClassName="text-neutral-500"
                            topLevel={<FromNow>{row.original.lastOrderDate}</FromNow>}
                            bottomLevel={<DateText date={row.original.lastOrderDate} />}
                        />
                    ) : null;
                },
                meta: {
                    aiChat: { label: t('table_header__last_order_date', { ns: 'customers-list' }) },
                    sortBy: CustomerSortBy.LAST_ORDER_DATE,
                    align: 'left',
                },
            },
            {
                accessorKey: 'rep_name',
                meta: {
                    enabled: canReadSalesRepAssignment,
                    aiChat: {
                        description: 'All sales reps assigned to the customer',
                        requires: ['canReadSalesRepAssignment'],
                    },
                },
                header: t('table_header__primary_rep', {
                    ns: 'customers-list',
                }),
                cell: ({ row }) => {
                    const { salesReps } = row.original;
                    return (
                        <span className="truncate">
                            {salesReps.length > 0
                                ? salesReps
                                      .map(
                                          (rep: { salesRepId: string; salesRepName: string }) =>
                                              `${rep.salesRepId}: ${rep.salesRepName}`,
                                      )
                                      .join(', ')
                                : '-'}
                        </span>
                    );
                },
            },
            {
                accessorKey: 'address.city',
                header: t('table_header__city', {
                    ns: 'customers-list',
                }),
            },
            {
                accessorKey: 'industrySector',
                header: t('table_header__industry', {
                    ns: 'customers-list',
                }),
                cell: ({ cell, row }) => (row.original.industrySector ? cell.renderValue() : '-'),
            },
            // Gross Margin TTM columns
            {
                accessorKey: 'grossMarginTtm',
                header: () => (
                    <MaskedContent>{t('table_header__gm_ttm', { ns: 'customers-list' })}</MaskedContent>
                ),
                cell: ({ row }) => (
                    <MaskedContent>
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>{row.original.grossMarginTtm}</CurrencyOrEmpty>
                        </DataText>
                    </MaskedContent>
                ),
                meta: {
                    sortBy: CustomerSortBy.GROSS_MARGIN_TTM,
                    enabled: canReadGrossMargin && showTtmColumns,
                    aiChat: {
                        label: t('table_header__gm_ttm', { ns: 'customers-list' }),
                        requires: ['canReadGrossMargin'],
                    },
                    align: 'right',
                    summary: (data) => (
                        <MaskedContent>
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>{data.grossMarginTtm}</CurrencyOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    hint: <MetricHintExplanation base={billingStatus} metric="GROSS_MARGIN" />,
                    onHintOpen: () => {
                        trackMetricHintOpened('GROSS_MARGIN');
                    },
                },
            },
            {
                accessorKey: 'grossMarginTtmPercent',
                header: () => (
                    <MaskedContent>
                        {t('table_header__gm_ttm_percent', { ns: 'customers-list' })}
                    </MaskedContent>
                ),
                cell: ({ row }) => (
                    <MaskedContent>
                        <DataText>
                            <MarginPercentage
                                margin={row.original.grossMarginTtm}
                                revenue={row.original.revenueTtm}
                            />
                        </DataText>
                    </MaskedContent>
                ),
                meta: {
                    enabled: canReadGrossMargin && showTtmColumns,
                    aiChat: {
                        label: t('table_header__gm_ttm_percent', { ns: 'customers-list' }),
                        requires: ['canReadGrossMargin'],
                    },
                    align: 'right',
                    hint: <MetricHintExplanation base={billingStatus} metric="GROSS_MARGIN_PERCENTAGE" />,
                    onHintOpen: () => {
                        trackMetricHintOpened('GROSS_MARGIN_PERCENTAGE');
                    },
                    summary: (data) => (
                        <MaskedContent>
                            <DataText>
                                <MarginPercentage margin={data.grossMarginTtm} revenue={data.revenueTtm} />
                            </DataText>
                        </MaskedContent>
                    ),
                },
                size: 130,
            },
            {
                accessorKey: 'deltaGmTtm',
                header: () => (
                    <MaskedContent>{t('table_header__gm_ttm_delta', { ns: 'customers-list' })}</MaskedContent>
                ),
                cell: ({ row }) => {
                    const amount = row.original.deltaGmTtm;
                    const up = amount === 0 ? null : amount > 0;
                    return (
                        <MaskedContent>
                            <DoubleDeckerCell
                                topLevel={
                                    <DataText>
                                        <DeltaText up={up}>
                                            <PercentageOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                {row.original.deltaGmPercentTtm}
                                            </PercentageOrEmpty>
                                            {up !== null && <DeltaTriangle up={up} />}
                                        </DeltaText>
                                    </DataText>
                                }
                                bottomLevel={
                                    <DataText>
                                        <CurrencyOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                            {row.original.deltaGmTtm}
                                        </CurrencyOrEmpty>
                                    </DataText>
                                }
                            />
                        </MaskedContent>
                    );
                },
                meta: {
                    sortBy: CustomerSortBy.GROSS_MARGIN_TTM_DELTA,
                    enabled: canReadGrossMargin && showTtmColumns,
                    aiChat: {
                        label: t('table_header__gm_ttm_delta', { ns: 'customers-list' }),
                        description:
                            'Change in gross margin versus the same 12 months a year earlier, as amount and percentage',
                        requires: ['canReadGrossMargin'],
                    },
                    align: 'right',
                    summary: (data) => {
                        const amount = data.deltaGmTtm;
                        const up = amount === 0 ? null : amount > 0;
                        return (
                            <MaskedContent>
                                <DoubleDeckerCell
                                    topLevel={
                                        <DataText>
                                            <DeltaText up={up}>
                                                <PercentageOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                    {data.deltaGmPercentTtm}
                                                </PercentageOrEmpty>
                                                {up !== null && <DeltaTriangle up={up} />}
                                            </DeltaText>
                                        </DataText>
                                    }
                                    bottomLevel={
                                        <DataText>
                                            <CurrencyOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                {data.deltaGmTtm}
                                            </CurrencyOrEmpty>
                                        </DataText>
                                    }
                                />
                            </MaskedContent>
                        );
                    },
                },
            },
            // Gross Margin YTD columns
            {
                accessorKey: 'grossMarginYtdThisYear',
                header: () => (
                    <MaskedContent>{t('table_header__gm_ytd', { ns: 'customers-list' })}</MaskedContent>
                ),
                cell: ({ row }) => (
                    <MaskedContent>
                        <DataText>
                            <CurrencyOrEmpty showEmptyIfZero>
                                {row.original.grossMarginYtdThisYear}
                            </CurrencyOrEmpty>
                        </DataText>
                    </MaskedContent>
                ),
                meta: {
                    sortBy: CustomerSortBy.GROSS_MARGIN_YTD,
                    enabled: canReadGrossMargin && showYtdColumns,
                    aiChat: {
                        label: t('table_header__gm_ytd', { ns: 'customers-list' }),
                        requires: ['canReadGrossMargin'],
                    },
                    align: 'right',
                    summary: (data) => (
                        <MaskedContent>
                            <DataText>
                                <CurrencyOrEmpty showEmptyIfZero>
                                    {data.grossMarginYtdThisYear}
                                </CurrencyOrEmpty>
                            </DataText>
                        </MaskedContent>
                    ),
                    hint: <MetricHintExplanation base={billingStatus} metric="GROSS_MARGIN" />,
                    onHintOpen: () => {
                        trackMetricHintOpened('GROSS_MARGIN');
                    },
                },
            },
            {
                accessorKey: 'grossMarginYtdPercent',
                header: () => (
                    <MaskedContent>
                        {t('table_header__gm_ytd_percent', { ns: 'customers-list' })}
                    </MaskedContent>
                ),
                cell: ({ row }) => (
                    <MaskedContent>
                        <DataText>
                            <MarginPercentage
                                margin={row.original.grossMarginYtdThisYear}
                                revenue={row.original.revenueYtdThisYear}
                            />
                        </DataText>
                    </MaskedContent>
                ),
                meta: {
                    enabled: canReadGrossMargin && showYtdColumns,
                    aiChat: {
                        label: t('table_header__gm_ytd_percent', { ns: 'customers-list' }),
                        requires: ['canReadGrossMargin'],
                    },
                    align: 'right',
                    hint: <MetricHintExplanation base={billingStatus} metric="GROSS_MARGIN_PERCENTAGE" />,
                    onHintOpen: () => {
                        trackMetricHintOpened('GROSS_MARGIN_PERCENTAGE');
                    },
                    summary: (data) => (
                        <MaskedContent>
                            <DataText>
                                <MarginPercentage
                                    margin={data.grossMarginYtdThisYear}
                                    revenue={data.revenueYtdThisYear}
                                />
                            </DataText>
                        </MaskedContent>
                    ),
                },
                size: 100,
            },
            {
                accessorKey: 'gmYtdDeltaAmount',
                header: () => (
                    <MaskedContent>{t('table_header__gm_ytd_delta', { ns: 'customers-list' })}</MaskedContent>
                ),
                cell: ({ row }) => {
                    const amount = row.original.gmYtdDeltaAmount;
                    const up = amount === 0 ? null : amount > 0;
                    return (
                        <MaskedContent>
                            <DoubleDeckerCell
                                topLevel={
                                    <DataText>
                                        <DeltaText up={up}>
                                            <PercentageOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                {row.original.gmYtdDeltaPercentage}
                                            </PercentageOrEmpty>
                                            {up !== null && <DeltaTriangle up={up} />}
                                        </DeltaText>
                                    </DataText>
                                }
                                bottomLevel={
                                    <DataText>
                                        <CurrencyOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                            {row.original.gmYtdDeltaAmount}
                                        </CurrencyOrEmpty>
                                    </DataText>
                                }
                            />
                        </MaskedContent>
                    );
                },
                meta: {
                    sortBy: CustomerSortBy.GROSS_MARGIN_YTD_DELTA,
                    enabled: canReadGrossMargin && showYtdColumns,
                    aiChat: {
                        label: t('table_header__gm_ytd_delta', { ns: 'customers-list' }),
                        description:
                            'Change in gross margin versus the same period last year, as amount and percentage',
                        requires: ['canReadGrossMargin'],
                    },
                    align: 'right',
                    summary: (data) => {
                        const amount = data.gmYtdDeltaAmount;
                        const up = amount === 0 ? null : amount > 0;
                        return (
                            <MaskedContent>
                                <DoubleDeckerCell
                                    topLevel={
                                        <DataText>
                                            <DeltaText up={up}>
                                                <PercentageOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                    {data.gmYtdDeltaPercentage}
                                                </PercentageOrEmpty>
                                                {up !== null && <DeltaTriangle up={up} />}
                                            </DeltaText>
                                        </DataText>
                                    }
                                    bottomLevel={
                                        <DataText>
                                            <CurrencyOrEmpty showEmptyIfZero showPlusSignIfPositive>
                                                {data.gmYtdDeltaAmount}
                                            </CurrencyOrEmpty>
                                        </DataText>
                                    }
                                />
                            </MaskedContent>
                        );
                    },
                },
            },
        ],
        [
            canReadGrossMargin,
            canReadSalesRepAssignment,
            isOrderSumMetric,
            enablePlatoStatus,
            isManagerView,
            sortingField,
            t,
            trackVisitTooltipOpened,
            showTtmColumns,
            showYtdColumns,
            billingStatus,
            trackMetricHintOpened,
        ],
    );

    return customerColumns;
}
